import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, X, Volume2, VolumeX, SkipForward, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface MediaFile {
  type: 'image' | 'video';
  url?: string;
  path?: string;
  duration?: number;
}

interface HopeReelData {
  id: string;
  title: string;
  affirmations: string[];
  media_files: MediaFile[];
  audio_file?: string;
}

interface HopeReelPlayerProps {
  reel: HopeReelData;
  onClose: () => void;
}

export default function HopeReelPlayer({ reel, onClose }: HopeReelPlayerProps) {
  // Early return if reel is invalid
  if (!reel || !reel.media_files || reel.media_files.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4 text-foreground">No media files found</p>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    );
  }

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [mediaDuration] = useState(5000); // 5 seconds per media item for hope reels
  const [audioMuted, setAudioMuted] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textKey, setTextKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Load media URLs from storage
  useEffect(() => {
    const loadMediaUrls = async () => {
      try {
        setIsLoading(true);
        
        if (reel.id === 'preview') {
          // For preview, use existing URLs
          const urls = reel.media_files?.map(mf => mf.url || '') || [];
          setMediaUrls(urls);
          if (reel.audio_file) {
            setAudioUrl(reel.audio_file);
          }
          setIsLoading(false);
          return;
        }

        // For saved reels, get URLs from storage
        const urls = await Promise.all(
          (reel.media_files || []).map(async (mediaFile) => {
            if (mediaFile.path) {
              const { data } = await supabase.storage
                .from('hope-reel-media')
                .createSignedUrl(mediaFile.path, 3600); // 1 hour expiry
              return data?.signedUrl || '';
            }
            return '';
          })
        );
        setMediaUrls(urls);

        if (reel.audio_file) {
          const { data } = await supabase.storage
            .from('hope-reel-audio')
            .createSignedUrl(reel.audio_file, 3600);
          setAudioUrl(data?.signedUrl || '');
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading media URLs:', error);
        setIsLoading(false);
      }
    };

    loadMediaUrls();
  }, [reel]);

  const currentMediaFile = reel.media_files?.[currentMediaIndex];
  const currentMediaUrl = mediaUrls[currentMediaIndex];

  const getTransitionClass = () => {
    const transitions = ['fade-in', 'scale-in'];
    const randomTransition = transitions[Math.floor(Math.random() * transitions.length)];
    return `animate-${randomTransition}`;
  };

  const playNext = () => {
    setIsTransitioning(true);
    
    setTimeout(() => {
      // Move to next media or loop back to start
      const nextIndex = currentMediaIndex < reel.media_files.length - 1 
        ? currentMediaIndex + 1 
        : 0;
      
      setCurrentMediaIndex(nextIndex);
      
      // Rotate through affirmations
      if (reel.affirmations.length > 0) {
        setCurrentAffirmationIndex(prev => 
          (prev + 1) % reel.affirmations.length
        );
      }
      
      setTextKey(prev => prev + 1);
      setIsTransitioning(false);
    }, 400);
  };

  const startPlayback = () => {
    setIsPlaying(true);
    
    // Start audio if available
    if (audioRef.current && audioUrl && !audioMuted) {
      audioRef.current.play().catch(() => {
        // Audio autoplay blocked - user interaction required
      });
    }

    // Clear any existing intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start slideshow
    intervalRef.current = setInterval(() => {
      if (!isPlaying) return;
      playNext();
    }, mediaDuration);
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
    }

    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const resetPlayback = () => {
    pausePlayback();
    setCurrentMediaIndex(0);
    setCurrentAffirmationIndex(0);
    setTextKey(0);
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      pausePlayback();
    } else {
      startPlayback();
    }
  };

  const toggleAudio = () => {
    setAudioMuted(!audioMuted);
    if (audioRef.current) {
      audioRef.current.muted = !audioMuted;
    }
  };

  // Handle video ended event
  const handleVideoEnded = () => {
    if (isPlaying) {
      playNext();
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Clean up when isPlaying changes
  useEffect(() => {
    if (!isPlaying && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isPlaying]);

  if (isLoading || !reel.media_files || reel.media_files.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Loading your Hope Reel...</p>
        </div>
      </div>
    );
  }

  if (!currentMediaUrl && !isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4 text-foreground">Unable to load media</p>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-therapeutic-dark via-background to-therapeutic-light text-white relative overflow-hidden">
      {/* Audio */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          loop
          muted={audioMuted}
          className="hidden"
        />
      )}

      {/* Background Media with Beautiful Transitions */}
      <div className="absolute inset-0">
        <div className={`w-full h-full ${
          isTransitioning 
            ? 'opacity-0 scale-95' 
            : `opacity-100 scale-100 ${getTransitionClass()}`
        } transition-all duration-1000 ease-out`}>
          {currentMediaFile?.type === 'image' ? (
            <div className="relative w-full h-full overflow-hidden">
              <img
                key={`img-${currentMediaIndex}-${textKey}`}
                src={currentMediaUrl}
                alt="Hope Memory"
                className="w-full h-full object-cover animate-scale-in"
                style={{
                  filter: 'brightness(0.85) contrast(1.1) saturate(1.2)',
                  objectPosition: 'center',
                }}
              />
              {/* Soft vignette effect */}
              <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/10 to-black/40" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
            </div>
          ) : (
            <div className="relative w-full h-full overflow-hidden">
              <video
                key={`video-${currentMediaIndex}-${textKey}`}
                ref={videoRef}
                src={currentMediaUrl}
                className="w-full h-full object-cover"
                autoPlay={isPlaying}
                muted={true}
                playsInline
                onEnded={handleVideoEnded}
                style={{
                  filter: 'brightness(0.85) contrast(1.1) saturate(1.2)',
                }}
              />
              {/* Soft vignette effect */}
              <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/10 to-black/40" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
            </div>
          )}
        </div>
      </div>

      {/* Floating Hope Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/20 rounded-full animate-pulse blur-sm"></div>
        <div className="absolute top-3/4 right-1/3 w-1.5 h-1.5 bg-primary/25 rounded-full animate-pulse blur-sm" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 right-1/4 w-2.5 h-2.5 bg-primary/15 rounded-full animate-pulse blur-sm" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/3 left-3/4 w-1 h-1 bg-primary/30 rounded-full animate-pulse blur-sm" style={{animationDelay: '3s'}}></div>
      </div>

      {/* Title Overlay at Top */}
      <div className="absolute top-8 left-0 right-0 z-20 px-8">
        <div 
          key={`top-title-${textKey}`}
          className="text-center animate-fade-in"
        >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Heart className="w-6 h-6 text-primary" />
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-primary-light to-primary bg-clip-text text-transparent tracking-wide drop-shadow-xl">
                {reel.title}
              </h1>
              <Heart className="w-6 h-6 text-primary" />
            </div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto animate-pulse"></div>
        </div>
      </div>

      {/* Main Affirmation Center Display */}
      {reel.affirmations.length > 0 && (
        <div className="absolute inset-0 flex items-center justify-center p-8 text-center z-10">
          <div className="max-w-6xl">
            <div 
              key={`affirmation-${textKey}-${currentAffirmationIndex}`}
              className="animate-fade-in"
            >
              {/* Gentle Word Emphasis */}
              <div className="mb-8">
                {reel.affirmations[currentAffirmationIndex].split(' ').map((word, index) => (
                  <span 
                    key={index}
                    className="inline-block mr-3 mb-2 text-3xl md:text-5xl lg:text-6xl font-light text-white drop-shadow-2xl tracking-wide animate-fade-in"
                    style={{ 
                      animationDelay: `${index * 0.15}s`,
                      textShadow: '2px 2px 8px rgba(0,0,0,0.7)'
                    }}
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>

            {/* Hope-themed Inspirational Styling */}
            <div 
              key={`decoration-${textKey}`}
              className="animate-fade-in"
              style={{ animationDelay: '1s' }}
            >
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-primary to-transparent mx-auto animate-pulse"></div>
              <div className="text-primary text-lg mt-6 font-light tracking-[0.3em] uppercase opacity-90 drop-shadow-lg">
                ✨ You Are Enough ✨
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2">
        <div className="flex items-center space-x-3 bg-black/30 backdrop-blur-md rounded-full px-6 py-3 border border-white/10">
          {reel.media_files.map((_, index) => (
            <div
              key={index}
              className={`h-1 rounded-full transition-all duration-500 ${
                index === currentMediaIndex 
                  ? 'w-8 bg-gradient-to-r from-primary to-primary-light shadow-lg shadow-primary/30' 
                  : index < currentMediaIndex
                  ? 'w-6 bg-primary/50'
                  : 'w-4 bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Elegant Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="bg-black/50 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl">
          <div className="flex items-center space-x-6 px-8 py-4">
            <Button
              onClick={togglePlay}
              size="lg"
              className="rounded-full w-16 h-16 bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary shadow-lg shadow-primary/30 transition-all duration-300"
            >
              {isPlaying ? (
                <Pause className="w-7 h-7" />
              ) : (
                <Play className="w-7 h-7 ml-1" />
              )}
            </Button>
            
            <Button
              onClick={resetPlayback}
              variant="outline"
              size="sm"
              className="border-white/40 text-white hover:bg-white/20 hover:border-white/60 rounded-xl transition-all duration-300"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restart
            </Button>

            {audioUrl && (
              <Button
                onClick={toggleAudio}
                variant="outline"
                size="sm"
                className="border-white/40 text-white hover:bg-white/20 hover:border-white/60 rounded-xl transition-all duration-300"
              >
                {audioMuted ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Close Button */}
      <Button
        onClick={onClose}
        variant="outline"
        size="sm"
        className="absolute top-8 right-8 border-white/30 text-white hover:bg-white/10 hover:border-white/50 rounded-xl backdrop-blur-md bg-black/30 transition-all duration-300 z-50"
      >
        <X className="w-4 h-4 mr-2" />
        Close
      </Button>
    </div>
  );
}